import React, { useState } from "react";
import { EmptyState } from "../shared";
import { TextInput } from "../atoms";
import {
  useGetTaskLabelsQuery,
  useCreateTaskLabelMutation,
  useUpdateTaskLabelMutation,
  useDeleteTaskLabelMutation,
} from "../../store";
import useAuth from "../../Hooks/useAuth";
import { useAlert } from "../../context/AlertContext";
import LabelChip from "./atoms/LabelChip";
import ColorPalette from "./atoms/ColorPalette";
import TaskConfirmModal from "./TaskConfirmModal";

const errText = (e) => e?.data?.message || e?.message || "İşlem başarısız.";
const btn = "px-3 py-1.5 rounded-lg text-sm font-Medium disabled:opacity-50";

function LabelRow({ label, onSave, onDelete, busy }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(label.name);
  const [color, setColor] = useState(label.color);

  if (!editing) {
    return (
      <li className="flex items-center justify-between gap-3 px-3 py-2">
        <LabelChip label={label} />
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setEditing(true)} className={`${btn} text-gray-600 hover:bg-gray-100`}>
            Düzenle
          </button>
          <button type="button" onClick={() => onDelete(label)} className={`${btn} text-fifth hover:bg-red-50`}>
            Sil
          </button>
        </div>
      </li>
    );
  }
  const trimmed = name.trim();
  const save = async () => {
    if (!trimmed) return;
    const ok = await onSave(label._id, { name: trimmed, color });
    if (ok) setEditing(false);
  };
  return (
    <li className="px-3 py-3 space-y-2">
      <TextInput
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && save()}
        maxLength={40}
        placeholder="Etiket adı"
      />
      <ColorPalette value={color} onChange={setColor} />
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => {
            setEditing(false);
            setName(label.name);
            setColor(label.color);
          }}
          className={`${btn} text-gray-600 hover:bg-gray-100`}
        >
          Vazgeç
        </button>
        <button type="button" disabled={!trimmed || busy} onClick={save} className={`${btn} bg-main text-white`}>
          Kaydet
        </button>
      </div>
    </li>
  );
}

// Etiket yönetimi (tasks.admin): oluştur, yeniden adlandır, renk değiştir, sil.
function LabelManager() {
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { showError, showSuccess } = useAlert();
  const { data, isLoading } = useGetTaskLabelsQuery({ token });
  const [createLabel, { isLoading: creating }] = useCreateTaskLabelMutation();
  const [updateLabel, { isLoading: updating }] = useUpdateTaskLabelMutation();
  const [deleteLabel, { isLoading: deleting }] = useDeleteTaskLabelMutation();
  const labels = data?.data?.labels || [];

  const [name, setName] = useState("");
  const [color, setColor] = useState("blue");
  const [error, setError] = useState("");
  const [toDelete, setToDelete] = useState(null);

  const create = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setError("");
    try {
      await createLabel({ token, name: trimmed, color }).unwrap();
      setName("");
    } catch (e) {
      setError(errText(e));
      showError(errText(e));
    }
  };

  const save = async (id, patch) => {
    try {
      await updateLabel({ token, id, ...patch }).unwrap();
      return true;
    } catch (e) {
      showError(errText(e));
      return false;
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteLabel({ token, id: toDelete._id }).unwrap();
      showSuccess("Etiket silindi.");
      setToDelete(null);
    } catch (e) {
      showError(errText(e));
    }
  };

  return (
    <section data-testid="label-manager">
      <h3 className="text-base font-SemiBold text-gray-800 mb-3">Etiketler</h3>

      <div className="rounded-xl border border-gray-200 p-3 mb-3 space-y-2">
        <TextInput
          label="Yeni etiket"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && create()}
          maxLength={40}
          placeholder="Etiket adı"
          error={Boolean(error)}
          errorMessage={error}
        />
        <ColorPalette value={color} onChange={setColor} />
        <div className="flex justify-end">
          <button
            type="button"
            disabled={!name.trim() || creating}
            onClick={create}
            className={`${btn} bg-main text-white`}
          >
            Etiket Ekle
          </button>
        </div>
      </div>

      {isLoading ? null : labels.length === 0 ? (
        <EmptyState message="Henüz etiket yok" />
      ) : (
        <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200">
          {labels.map((l) => (
            <LabelRow key={l._id} label={l} onSave={save} onDelete={setToDelete} busy={updating} />
          ))}
        </ul>
      )}

      <TaskConfirmModal
        open={Boolean(toDelete)}
        title="Etiketi sil"
        message={`"${toDelete?.name || ""}" etiketi silinecek ve işlerden çıkarılacak. Emin misiniz?`}
        busy={deleting}
        onConfirm={confirmDelete}
        onClose={() => setToDelete(null)}
      />
    </section>
  );
}

export default LabelManager;
